import { useQuery } from '@tanstack/react-query';
import { fetchApi } from '@/api/client';
import type { TokensResponse } from '@/api/types';
import { CHAIN_ID, EXPLORER_BASE } from '@/config';
import { listCwChains } from '@/data/deployment-status';

function explorerToken(addr: string) {
  return `${EXPLORER_BASE}/token/${addr}`;
}

export function TokensPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['tokens', CHAIN_ID],
    queryFn: () =>
      fetchApi<TokensResponse>(`/api/v1/tokens?chainId=${CHAIN_ID}&limit=200&includeDodoPool=true`),
    staleTime: 30_000,
  });

  const cwChains = listCwChains();

  const cStar =
    data?.tokens?.filter((t) => (t.symbol || '').toLowerCase().startsWith('c')) || [];

  return (
    <>
      <h1>c* &amp; cW* tokens</h1>
      <p className="lead">
        <span className="badge badge-c">c*</span> Compliant tokens on Chain {CHAIN_ID} (from indexer).
        <br />
        <span className="badge badge-cw">cW*</span> Bridged compliant-wrapped tokens on public EVM chains
        (from repo <span className="mono">deployment-status.json</span>, rebuild site to refresh).
      </p>

      <h2>
        Chain {CHAIN_ID} — c* (live API){' '}
        {isLoading && <span className="muted">Loading…</span>}
      </h2>
      {error && (
        <div className="err-box">{(error as Error).message}</div>
      )}
      {!isLoading && !error && (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Name</th>
                <th>Address</th>
                <th>PMM</th>
              </tr>
            </thead>
            <tbody>
              {cStar.map((t) => (
                <tr key={t.address}>
                  <td>
                    <span className="badge badge-c">{t.symbol || '?'}</span>
                  </td>
                  <td>{t.name || '—'}</td>
                  <td className="mono">
                    <a href={explorerToken(t.address)} target="_blank" rel="noreferrer">
                      {t.address.slice(0, 10)}…{t.address.slice(-6)}
                    </a>
                  </td>
                  <td>{t.hasDodoPool || t.pmmPool ? 'Yes' : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h2>Bridged cW* (registry)</h2>
      <p className="muted">
        Addresses are maintained in <span className="mono">cross-chain-pmm-lps/config/deployment-status.json</span>.
      </p>
      {cwChains.map(({ chainId, name, tokens }) => (
        <div key={chainId} style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ fontSize: '1rem', margin: '0 0 0.5rem' }}>
            {name}{' '}
            <span className="muted mono">({chainId})</span>
          </h3>
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Symbol</th>
                  <th>Address</th>
                </tr>
              </thead>
              <tbody>
                {tokens.map((row) => (
                  <tr key={row.symbol}>
                    <td>
                      <span className="badge badge-cw">{row.symbol}</span>
                    </td>
                    <td className="mono">{row.address}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </>
  );
}
